import { t } from "i18next";
import React, { ReactElement } from "react";
import { onEnterPressed } from "~/src/helpers/accessibilityHelpers";
import { Favorittområde, Område } from "../../api/types";
import { ArtType, OmradeType } from "../../helpers/types";
import { useStateful } from "../../hooks/useStateful";
import { ArrowRightIcon, ArtIcons, artIcon, CheckIcon } from "../Icons/Icons";
import { ArtNames } from "../Jaktomrade/ArtNames";
import Text from "../Text/Text";

export type JaktområdeDefaultProps = {
  områdeEntry: Område | Favorittområde;
  omradeType?: OmradeType;
  selectable?: boolean;
  onClick?: () => void;
  isLink?: boolean;
  isSelected?: boolean;
  small?: boolean;
  overrideSelected?: boolean;
};

const JaktområdeDefault: React.ForwardRefRenderFunction<
  HTMLButtonElement,
  JaktområdeDefaultProps
> = (
  {
    områdeEntry,
    omradeType,
    selectable = true,
    onClick,
    isLink,
    isSelected,
    small,
    overrideSelected,
  },
  ref,
) => {
  const selected = useStateful(overrideSelected != undefined ? overrideSelected : false);
  const isFavorittomrade = "Områdenavn" in områdeEntry;
  const områdeNavn = isFavorittomrade
    ? områdeEntry.Områdenavn
    : "Navn" in områdeEntry
      ? områdeEntry.Navn
      : "Ukjent";
  const områdeKode = isFavorittomrade
    ? områdeEntry.Områdekode
    : "Id" in områdeEntry
      ? områdeEntry.Id
      : "Ukjent";

  const handleClicked = () => {
    selectable && selected.set(!selected.value);
    onClick?.();
  };

  const bgColor =
    "isPending" in områdeEntry && områdeEntry.isPending
      ? "bg-ldir-orange-800"
      : isLink === true
        ? "bg-ldir-blue-100 border-t border-ldir-blue-pale"
        : "";

  const getHeader = (): ReactElement => {
    return (
      <Text key={`header-${id}`} bold className="text-wrap">
        {områdeNavn}
      </Text>
    );
  };
  const getInfo = (): ReactElement => {
    return (
      <div key={`info-${id}`} className="flex flex-row">
        <Text>{områdeKode}</Text>
        {(omradeType === OmradeType.Vald && (
          <Text className="ml-3">{t("fauna.art.rådyr.artsnavn")}</Text>
        )) || (
          <Text className="flex flex-row items-center ml-3">{ArtNames({ ...områdeEntry })}</Text>
        )}
      </div>
    );
  };

  const id = `Jaktomrade-${områdeNavn}`;
  return (
    <button
      aria-label={`${områdeNavn} ${selected.value ? "valgt" : ""} ${isLink ? "lenke" : ""}`}
      id={id}
      type="button"
      key={id}
      className={`${bgColor} ${
        selected.value && " bg-ldir-green-800"
      } text-ldir-brown-900 w-full flex flex-wrap justify-between items-center py-2 ${
        small ? "px-2" : "px-4"
      }`}
      ref={ref}
      onClick={async () => handleClicked()}
      onKeyDown={(e) => onEnterPressed(e, handleClicked)}
    >
      <div className="flex items-start gap-4 flex-1">
        <div className="text-left ">
          {getHeader()}
          {getInfo()}
        </div>
      </div>
      {isLink != true && (
        <div className="flex">
          {(omradeType === OmradeType.Vald && (
            <div className="flex flex-row items-center">{artIcon(ArtType.Rådyr, true)}</div>
          )) ||
            ArtIcons({ ...områdeEntry, small: true })}
          {(selected.value || isSelected) && (
            <div className="flex items-center">
              <CheckIcon className="ml-2 " />
            </div>
          )}
        </div>
      )}
      {isLink === true && (
        <div className="flex items-center ">
          {(omradeType === OmradeType.Vald && (
            <div className="flex flex-row items-center">{artIcon(ArtType.Rådyr)}</div>
          )) ||
            ArtIcons({ ...områdeEntry })}
          <ArrowRightIcon className="ml-4 -mr-0.5" />
        </div>
      )}
    </button>
  );
};

export default React.forwardRef(JaktområdeDefault);
